import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Share2,
  Pencil,
  Flag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import StoryCardDialog from '@/components/StoryCardDialog';
import StoryAuthorCard from '@/components/StoryAuthorCard';
import StoryBody from '@/components/StoryBody';
import { useAuth } from '@/context/AuthContext';
import { getPublicStory, isCurrentUserStoryOwner } from '@/services/storyRepo';
import { reportStory, reanalyzeOneStory } from '@/services/storyService';
import { useTagOptions } from '@/hooks/useTagOptions';
import { FILTER_DIMENSIONS } from '@/lib/filters';
import DimensionInfoButton from '@/components/DimensionInfoButton';
import ReanalyzeFieldsPopover from '@/components/ReanalyzeFieldsPopover';
import Seo, { SITE_URL } from '@/components/Seo';

// Plain-English labels for the guna arc — the DB stores Sanskrit IDs
// (tamas/rajas/sattva) which read as jargon outside a yoga context.
// Emoji match the practice intake screens (sattvic 🌟, rajasic ⚡, tamasic 😶‍🌫️)
// so readers see a consistent visual language across the app.
const GUNA_DISPLAY = {
  tamas:  { emoji: '😶‍🌫️', label: 'Low & heavy' },
  rajas:  { emoji: '⚡',    label: 'Restless & busy' },
  sattva: { emoji: '🌟',    label: 'Calm & clear' },
};
import {
  DEFAULT_STORY_REPORT_REASON,
  STORY_REPORT_REASONS,
} from '@/lib/storyReportReasons';
import {
  STORY_REPORT_LOADING_HINT,
  STORY_REPORT_LOADING_INTERVAL_MS,
  STORY_REPORT_LOADING_MESSAGES,
} from '@/lib/storyReportLoadingMessages';

const StoryViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isModerator, loading: authLoading } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState(DEFAULT_STORY_REPORT_REASON);
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportMessageIdx, setReportMessageIdx] = useState(0);
  // Moderator-only single-story reanalyze (label drives the toast + button text).
  const [reanalyzingLabel, setReanalyzingLabel] = useState(null);
  const { tagOptions } = useTagOptions();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const fetched = await getPublicStory(id);
        if (cancelled) return;
        if (!fetched) {
          setLoadError('Story not found or not yet approved.');
          return;
        }
        setStory(fetched);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching story:', err);
        setLoadError(err?.message || 'Could not load this story');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const userId = user?.id;
  useEffect(() => {
    if (!id || !userId || authLoading || loadError) {
      setCanEdit(false);
      return undefined;
    }
    let cancelled = false;
    isCurrentUserStoryOwner(id).then((ok) => {
      if (!cancelled) setCanEdit(!!ok);
    });
    return () => { cancelled = true; };
  }, [id, userId, authLoading, loadError]);

  useEffect(() => {
    if (!reportSubmitting) {
      setReportMessageIdx(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setReportMessageIdx((prev) => (prev + 1) % STORY_REPORT_LOADING_MESSAGES.length);
    }, STORY_REPORT_LOADING_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [reportSubmitting]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const resetReportForm = () => {
    setReportReason(DEFAULT_STORY_REPORT_REASON);
    setReportDetails('');
  };

  const openReportDialog = () => {
    if (!user) {
      toast.info('Please sign in to report a story.');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    resetReportForm();
    setReportOpen(true);
  };

  // Moderator-only per-story reanalyze. Hits the same backend endpoint
  // as the batch dashboard action, scoped to this story_id. Server
  // honours field-selective semantics so any manual curation (e.g.
  // hand-added practice_ids) survives. We refetch the story after
  // success so the new tag arrays render without a hard reload.
  const handleReanalyzeOne = async ({ label, fields, rebuildChunks = false }) => {
    if (!id || reanalyzingLabel) return;
    const ok = window.confirm(
      `Re-run Gemini and overwrite ${label} on this story?\n\n`
      + `Fields that will be overwritten: ${fields.join(', ')}.\n\n`
      + 'All other columns (including any manually-added tags) are '
      + 'preserved. The story is also re-embedded.'
      + (rebuildChunks ? '\n\nAlso rebuilds this story’s semantic-chunk lane.' : '')
    );
    if (!ok) return;
    setReanalyzingLabel(label);
    try {
      const result = await reanalyzeOneStory(id, { fields, rebuildChunks });
      if ((result?.updated ?? 0) > 0) {
        toast.success(`${label} updated.`);
      } else if ((result?.failed ?? 0) > 0) {
        toast.error(`${label} update failed — see logs.`);
      } else {
        toast.info('Nothing changed.');
      }
      // Refetch so the UI reflects the new tags immediately. getPublicStory
      // returns the post-update view; tag chips on this page re-render
      // off it.
      try {
        const fetched = await getPublicStory(id);
        if (fetched) setStory(fetched);
      } catch (refetchErr) {
        console.warn('Reanalyze succeeded but refetch failed:', refetchErr);
      }
    } catch (err) {
      console.error('Reanalyze one-story failed:', err);
      toast.error(err?.message || 'Could not reanalyze this story.');
    } finally {
      setReanalyzingLabel(null);
    }
  };

  const submitReport = async () => {
    if (!id) return;
    const trimmedDetails = reportDetails.trim();
    if (reportReason === 'Other' && trimmedDetails.length < 10) {
      toast.error('Please add a short description (at least 10 characters) when you choose Other.');
      return;
    }
    setReportSubmitting(true);
    try {
      const data = await reportStory(id, reportReason, trimmedDetails);
      toast.success(data?.message || 'Thank you. A moderator will review this report.');
      setReportOpen(false);
      resetReportForm();
    } catch (err) {
      console.error('Report failed:', err);
      toast.error(err?.message || 'Could not submit report. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C05621]" />
          <p className="mt-4 text-[#6B665E]">Loading story...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-2xl text-[#2D2A26] font-serif">Couldn't load this story</p>
          <p className="text-sm text-[#6B665E] mt-2 break-words">{loadError}</p>
          <Button
            onClick={() => navigate('/')}
            className="mt-6 bg-[#C05621] hover:bg-[#A0461A] text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  if (!story) return null;

  const visibleSections = story.sections || [];

  // Resolve tag IDs → display names for the themes footer. Empty dimensions
  // are filtered out so the block collapses to nothing when the story has
  // no tags (older rows, AI extraction returned empty).
  const themeGroups = FILTER_DIMENSIONS
    .map(({ key, label, param }) => {
      const ids = story[key] || [];
      if (!ids.length) return null;
      const options = tagOptions[key] || [];
      const items = ids
        .map((id) => {
          const found = options.find((t) => t.id === id);
          return { id, label: found?.display_name || id };
        });
      return { key, label, param, items };
    })
    .filter(Boolean);

  const goToFilter = (param, tagId) => {
    navigate(`/search?${param}=${encodeURIComponent(tagId)}`);
  };

  const seoDescription = (
    story.highlight || story.preview || 'A story of inner transformation on Katha Mithra.'
  ).slice(0, 300);
  const storyJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: story.title,
    description: seoDescription,
    ...(story.created_at ? { datePublished: story.created_at } : {}),
    author: { '@type': 'Person', name: story.author_name || 'Anonymous' },
    publisher: {
      '@type': 'Organization',
      name: 'Katha Mithra',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/icon-512.png` },
    },
    mainEntityOfPage: `${SITE_URL}/story/${id}`,
    image: `${SITE_URL}/og-image.png`,
    ...(story.category ? { articleSection: story.category } : {}),
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title={`${story.title} · Katha Mithra`}
        description={seoDescription}
        path={`/story/${id}`}
        type="article"
        jsonLd={storyJsonLd}
      />
      <PageHeader>
        <ToolbarCaptionButton
          label="Back to Home"
          shortLabel="Home"
          onClick={() => navigate('/')}
          data-testid="back-home-btn"
        />
      </PageHeader>

      <article className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="story-content">
        <div className="mb-8">
          <span className="text-sm font-medium tracking-wide uppercase text-[#6B665E]">
            {story.category}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight leading-tight text-[#2D2A26] mt-3 mb-4 break-words">
            {story.title}
          </h1>
          <StoryAuthorCard
            authorName={story.author_name}
            avatarObjectKey={story.author_avatar_object_key}
            bio={story.author_bio}
          />
          {(canEdit || isModerator) && (
            <div className="flex flex-wrap items-center gap-2 mt-4 mb-2">
              {canEdit && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/story/${id}/edit`)}
                  className="border-[#C05621]/40 text-[#C05621] hover:bg-[#FBF4EB]"
                  data-testid="edit-story-btn"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit story
                </Button>
              )}
              {/* Moderator-only — re-extract a chosen set of fields on
                  this story without leaving the page. Multi-select via
                  the shared popover; same field-selective semantics as
                  the dashboard batch action. */}
              {isModerator && (
                <ReanalyzeFieldsPopover
                  onApply={handleReanalyzeOne}
                  running={!!reanalyzingLabel}
                  runningLabel={reanalyzingLabel ? `${reanalyzingLabel}…` : null}
                  triggerTestId="story-reanalyze-btn"
                  buttonClassName="border-[#C05621]/40 text-[#C05621] hover:bg-[#FBF4EB] h-9 px-3 text-sm"
                  align="start"
                />
              )}
            </div>
          )}

          {story.highlight && (
            <p
              className="mt-6 pl-4 border-l-4 border-[#C05621] text-[#2D2A26] italic text-lg leading-relaxed font-serif"
              data-testid="story-highlight"
            >
              {story.highlight}
            </p>
          )}
        </div>

        <div className="space-y-8 leading-loose text-[#2D2A26]">
          {visibleSections.length > 0 ? (
            visibleSections.map((sec) => (
              <section key={sec.key}>
                <h2 className="text-2xl md:text-3xl font-serif font-medium text-[#C05621] mb-4">
                  {sec.label}
                </h2>
                <StoryBody text={sec.content} />
              </section>
            ))
          ) : story.raw_text ? (
            <section>
              <StoryBody text={story.raw_text} />
            </section>
          ) : (
            <p className="text-[#6B665E]">This story is awaiting content.</p>
          )}
        </div>

        {(themeGroups.length > 0 || story.practice_freetext) && (
          <div
            className="mt-12 pt-8 border-t border-[#E6E2D6] space-y-4"
            data-testid="story-themes"
          >
            <h3 className="text-xl font-serif font-medium text-[#2D2A26]">Themes</h3>
            <div className="space-y-3">
              {themeGroups.map(({ key, label, param, items }) => (
                <div key={key} className="flex flex-col sm:flex-row sm:items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wider text-[#6B665E] shrink-0 sm:w-28 inline-flex items-center gap-1">
                    {label}
                    <DimensionInfoButton dimensionKey={key} />
                  </span>
                  {key === 'guna_arc' ? (
                    // Ordered trajectory — render as connected chips with
                    // arrows between, not a flat unordered chip list.
                    <div className="flex flex-wrap items-center gap-2">
                      {items.map((t, idx) => {
                        const meta = GUNA_DISPLAY[t.id] || { emoji: '', label: t.label };
                        return (
                          <div key={`${key}-${t.id}-${idx}`} className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => goToFilter(param, t.id)}
                              className="inline-flex items-center gap-1.5 rounded-full text-sm leading-normal px-3 py-1 bg-[#FBF4EB] text-[#2D2A26] border border-[#E6E2D6] hover:border-[#C05621] transition-colors"
                              data-testid={`story-theme-${key}-${t.id}-${idx}`}
                            >
                              <span aria-hidden="true">{meta.emoji}</span>
                              <span>{meta.label}</span>
                            </button>
                            {idx < items.length - 1 && (
                              <span className="text-[#6B665E] select-none" aria-hidden="true">→</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {items.map((t) => (
                        <button
                          key={`${key}-${t.id}`}
                          type="button"
                          onClick={() => goToFilter(param, t.id)}
                          className="rounded-full text-sm leading-normal px-3 py-1 bg-[#FBF4EB] text-[#2D2A26] border border-[#E6E2D6] hover:border-[#C05621] hover:bg-[#FBF4EB] transition-colors"
                          data-testid={`story-theme-${key}-${t.id}`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {story.practice_freetext && (
              <div
                className="flex flex-col sm:flex-row sm:items-baseline gap-2 pt-2"
                data-testid="story-practice-freetext"
              >
                <span className="text-xs uppercase tracking-wider text-[#6B665E] shrink-0 sm:w-28">
                  Also mentioned
                </span>
                <p className="text-sm text-[#6B665E] italic leading-relaxed">
                  {story.practice_freetext}
                </p>
              </div>
            )}
          </div>
        )}

        {!canEdit && (
          <div className="mt-12 pt-8 border-t border-[#E6E2D6]" data-testid="story-report-section">
            <h3 className="text-xl font-serif font-medium text-[#2D2A26] mb-2 flex items-center">
              <Flag className="h-5 w-5 mr-2 text-[#6B665E]" />
              Community safety
            </h3>
            <p className="text-sm text-[#6B665E] mb-4">
              If this story breaks our guidelines, you can flag it for moderator review. Reports are
              confidential.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={openReportDialog}
              className="border-[#E6E2D6] text-[#2D2A26] hover:bg-white"
              data-testid="report-story-btn"
            >
              <Flag className="h-4 w-4 mr-2" />
              Report this story
            </Button>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-[#E6E2D6]">
          <h3 className="text-xl font-serif font-medium text-[#2D2A26] mb-2 flex items-center">
            <Share2 className="h-5 w-5 mr-2" />
            Share this story
          </h3>
          <p className="text-sm text-[#6B665E] mb-4">
            Customize a story card, then share it as an image or caption to WhatsApp, Instagram, Facebook, X, or your phone's share sheet.
          </p>
          <Button
            onClick={() => setCardDialogOpen(true)}
            className="bg-[#C05621] hover:bg-[#A0461A] text-white"
            data-testid="share-story-btn"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share story
          </Button>
        </div>
      </article>

      <StoryCardDialog
        open={cardDialogOpen}
        onOpenChange={setCardDialogOpen}
        story={story}
        shareUrl={shareUrl}
      />

      <Dialog
        open={reportOpen}
        onOpenChange={(open) => {
          if (!open && reportSubmitting) return;
          setReportOpen(open);
          if (!open) resetReportForm();
        }}
      >
        <DialogContent
          data-testid="report-story-dialog"
          className="max-w-md"
          onPointerDownOutside={(e) => {
            if (reportSubmitting) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (reportSubmitting) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-[#2D2A26]">Report this story</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#6B665E]">
              Tell us what is wrong. Moderators will look at the story and take appropriate action.
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2D2A26]" htmlFor="report-reason">
                Reason
              </label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger id="report-reason" className="w-full" data-testid="report-reason-select">
                  <SelectValue placeholder="Choose a reason" />
                </SelectTrigger>
                <SelectContent>
                  {STORY_REPORT_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2D2A26]" htmlFor="report-details">
                Additional details (optional)
              </label>
              <Textarea
                id="report-details"
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder="Specific timestamps, quotes, or context help moderators."
                className="min-h-24"
                data-testid="report-details-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setReportOpen(false)}
              disabled={reportSubmitting}
              data-testid="report-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitReport}
              disabled={reportSubmitting}
              className="bg-[#C05621] hover:bg-[#A0461A] text-white"
              data-testid="report-submit-btn"
            >
              {reportSubmitting
                ? STORY_REPORT_LOADING_MESSAGES[reportMessageIdx]
                : 'Submit report'}
            </Button>
          </DialogFooter>
          {reportSubmitting && (
            <p className="text-sm text-[#6B665E] text-center -mt-2" data-testid="report-loading-hint">
              {STORY_REPORT_LOADING_HINT}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoryViewPage;
